import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useLanguage } from '../contexts/LanguageContext';

interface Product {
  id: number;
  name: string;
}

interface Language {
  id: number;
  code: string;
  name: string;
}

interface FormData {
  selectedProducts: Product[];
  selectedLanguage: Language | null;
}

const FormView: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    selectedProducts: [],
    selectedLanguage: null
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Załaduj języki przy starcie komponentu
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoadingData(true);
      
      // Załaduj języki
      const languagesResponse = await axios.get('/api/products/languages');
      if (languagesResponse.data.success) {
        setLanguages(languagesResponse.data.data);
      }
      
      // Załaduj początkową listę produktów (pierwsze 100)
      const productsResponse = await axios.get('/api/products/search?limit=100');
      if (productsResponse.data.success) {
        setProducts(productsResponse.data.data);
      }
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Błąd podczas ładowania danych');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Funkcja do wyszukiwania produktów
  const loadProducts = async (inputValue: string) => {
    try {
      const response = await axios.get(
        `/api/products/search?q=${encodeURIComponent(inputValue)}&limit=50`
      );
      
      if (response.data.success) {
        return response.data.data.map((product: Product) => ({
          value: product.id,
          label: product.name,
          ...product
        }));
      }
      return [];
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (formData.selectedProducts.length === 0) {
        setError('Proszę wybrać przynajmniej jeden produkt');
        return;
      }
      
      if (!formData.selectedLanguage) {
        setError('Proszę wybrać język');
        return;
      }

      const productIds = formData.selectedProducts.map(p => p.id);
      const response = await axios.post('/api/products/descriptions', {
        productIds,
        languageCode: formData.selectedLanguage.code
      });
      
      if (response.data.success) {
        // Przekaż dane przez state do ResponseView
        navigate('/response', { 
          state: { 
            descriptions: response.data.data,
            language: formData.selectedLanguage,
            requestedProducts: formData.selectedProducts
          }
        });
      } else {
        setError(response.data.message || 'Błąd podczas pobierania opisów');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Błąd sieci');
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="hero-section">
        <div className="loading">
          <div className="spinner"></div>
          Ładowanie danych...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section">
        <h1 className="hero-title">{t('heroTitle')}</h1>
        <p className="hero-subtitle">{t('heroSubtitle')}</p>
        <p className="hero-description">
          {t('heroDescription1')} <span className="highlight">{t('heroDescription2')}</span> <br />
          <span className="highlight">{t('heroDescription3')}</span> {t('heroDescription4')}
        </p>
      </section>

      {/* Form Section */}
      <section className="form-section">
        <div className="form-container">
          <h2 className="form-title">{t('formTitle')}</h2>
          
          {error && (
            <div className="error-message" style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px 20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #ef5350'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('selectProducts')}</label>
                <AsyncSelect
                  isMulti
                  name="products"
                  defaultOptions={products.map(product => ({
                    value: product.id,
                    label: product.name,
                    ...product
                  }))}
                  value={formData.selectedProducts.map(product => ({
                    value: product.id,
                    label: product.name,
                    ...product
                  }))}
                  onChange={(selectedOptions) => {
                    const selected = selectedOptions as any[];
                    setFormData(prev => ({
                      ...prev,
                      selectedProducts: selected || []
                    }));
                  }}
                  loadOptions={loadProducts}
                  placeholder={t('searchPlaceholder')}
                  isSearchable
                  isDisabled={loading}
                  className="form-select"
                  classNamePrefix="react-select"
                  noOptionsMessage={({ inputValue }) => 
                    inputValue ? `${t('noProducts')} "${inputValue}"` : t('noProductsAvailable')
                  }
                  loadingMessage={() => t('loading')}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('selectLanguage')}</label>
                <Select
                  name="language"
                  options={languages.map(language => ({
                    value: language.code,
                    label: language.name,
                    ...language
                  }))}
                  value={formData.selectedLanguage ? {
                    value: formData.selectedLanguage.code,
                    label: formData.selectedLanguage.name,
                    ...formData.selectedLanguage
                  } : null}
                  onChange={(selectedOption) => {
                    setFormData(prev => ({
                      ...prev,
                      selectedLanguage: selectedOption as Language
                    }));
                  }}
                  placeholder={t('languagePlaceholder')}
                  isSearchable
                  isDisabled={loading}
                  className="form-select"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => t('noLanguages')}
                />
              </div>

              <button 
                type="submit" 
                disabled={loading || formData.selectedProducts.length === 0 || !formData.selectedLanguage}
                className="submit-button"
              >
                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                    {t('loadingButton')}
                  </div>
                ) : (
                  <>
                    {t('downloadButton')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default FormView;
